import { useState } from "react"

interface Column {
  id: string
  label: string
  isVisible: boolean
  isPinned?: boolean
  width?: string
  isAlwaysVisible?: boolean
  [key: string]: any // Allow additional properties for flexibility (e.g., accessor, cellType, render for clients)
}

interface UseColumnToggleOptions {
  initialColumns: Column[]
  enablePin?: boolean // Allow pin/unpin functionality (for clients page)
}

export function useColumnToggle({ initialColumns, enablePin = false }: UseColumnToggleOptions) {
  const [columns, setColumns] = useState<Column[]>(initialColumns)

  const handleColumnChange = (columnId: string, action: "toggle" | "pin" | "unpin") => {
    setColumns(
      columns.map((col) => {
        if (col.id === columnId) {
          if (action === "toggle") {
            // Prevent hiding always-visible columns
            if (col.isAlwaysVisible) {
              return col
            }
            // Prevent hiding the last visible column
            const visibleCount = columns.filter((c) => c.isVisible).length
            if (!col.isVisible || visibleCount > 1) {
              return { ...col, isVisible: !col.isVisible }
            }
            // If trying to hide the last visible column, don't change it
            return col
          }
          // Handle pin/unpin only if enabled
          if (enablePin) {
            if (action === "pin") {
              return { ...col, isPinned: true }
            } else if (action === "unpin") {
              return { ...col, isPinned: false }
            }
          }
        }
        return col
      }),
    )
  }

  const handleSelectAllColumns = (select: boolean) => {
    // Prevent hiding all columns - always-visible columns must remain visible
    if (!select) {
      const alwaysVisibleColumns = columns.filter((col) => col.isAlwaysVisible)
      const otherVisibleCount = columns.filter((col) => col.isVisible && !col.isAlwaysVisible).length
      
      // For clients page: check if there are always-visible columns
      if (enablePin) {
        const hasAlwaysVisible = columns.some((col) => col.isAlwaysVisible)
        if (!hasAlwaysVisible) {
          return // Don't allow hiding all columns if no always-visible columns exist
        }
      } else {
        // For other pages: check if only always-visible columns remain
        if (otherVisibleCount === 0 && alwaysVisibleColumns.length > 0) {
          return // Don't hide all columns if only always-visible columns remain
        }
      }
    }
    setColumns(columns.map((col) => ({ 
      ...col, 
      isVisible: col.isAlwaysVisible ? true : select 
    })))
  }

  return {
    columns,
    setColumns,
    handleColumnChange,
    handleSelectAllColumns,
  }
}

